// RoofingJobs — Job Radar (pipeline) + AI Diagnose + Photo-to-Shingle
// ID + Branded Estimate PDF. Mirrors the pest/HVAC pattern.
import { useEffect, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import { toast } from "sonner";
import CatalogAutocomplete from "@/components/CatalogAutocomplete";
import { matchRoofingSystems, ROOFING_SYSTEMS } from "@/data/roofingSystems";
import { ROOFING_COMMON_FIXES, protocolsForRoofKind } from "@/data/roofingCommonFixes";
import { Wrench } from "@phosphor-icons/react";
import {
  House, Plus, Trash, X, MapPinLine, Camera, Sparkle, FilePdf,
  CaretRight, CurrencyDollar, Warning, CloudRain, ChatCircleText,
} from "@phosphor-icons/react";

const STATUS_META = {
  lead:        { color: "bg-slate-400 text-white",     label: "LEAD" },
  quoted:      { color: "bg-cyan-500 text-white",      label: "QUOTED" },
  scheduled:   { color: "bg-fuchsia-500 text-white",   label: "SCHEDULED" },
  in_progress: { color: "bg-amber-400 text-slate-900", label: "IN PROGRESS" },
  complete:    { color: "bg-emerald-500 text-white",   label: "COMPLETE" },
  declined:    { color: "bg-red-500 text-white",       label: "DECLINED" },
};
const JOB_TYPE_LABELS = {
  inspection: "Inspection", repair: "Repair", replacement: "Replacement",
  hail_claim: "Hail Claim", storm_damage: "Storm Damage", leak: "Leak",
  gutter: "Gutter", other: "Other",
};
const ROOF_TYPE_LABELS = {
  asphalt_3tab: "Asphalt 3-Tab", asphalt_architectural: "Asphalt Architectural",
  metal_standing_seam: "Metal Standing Seam", metal_corrugated: "Metal Corrugated",
  clay_tile: "Clay Tile", concrete_tile: "Concrete Tile",
  slate: "Slate", wood_shake: "Wood Shake",
  flat_epdm: "Flat EPDM", flat_tpo: "Flat TPO",
  flat_modified_bitumen: "Flat Mod. Bitumen", other: "Other",
};

const initialJob = {
  customer_name: "", customer_phone: "", property_address: "",
  job_type: "repair", status: "lead", roof_type: "asphalt_architectural",
  sq_footage: "", stories: "", estimated_amount: "",
  scheduled_date: "", notes: "",
};

export default function RoofingJobs() {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(initialJob);
  const [saving, setSaving] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagForm, setDiagForm] = useState({ roof_type: "asphalt_architectural", roof_age_years: "", symptoms: "", sq_footage: "", notes: "" });
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagResult, setDiagResult] = useState(null);
  const [idLoading, setIdLoading] = useState(false);
  const [idResult, setIdResult] = useState(null);
  const [idPreview, setIdPreview] = useState(null);
  const [linkJobId, setLinkJobId] = useState("");
  const [journalFor, setJournalFor] = useState(null);
  const [journalData, setJournalData] = useState(null);
  const [activeJob, setActiveJob] = useState(() => {
    try { return JSON.parse(localStorage.getItem("roof_active_job") || "null"); } catch { return null; }
  });
  useEffect(() => { if (activeJob?.id) setLinkJobId(activeJob.id); }, [activeJob]);

  const load = async () => { const r = await api.get("/roofing/jobs"); setData(r.data); };
  useEffect(() => { load(); }, []);
  const setF = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!form.customer_name || !form.property_address) {
      toast.error("Customer name and property address required"); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        sq_footage: form.sq_footage ? parseFloat(form.sq_footage) : null,
        stories: form.stories ? parseInt(form.stories, 10) : null,
        estimated_amount_cents: form.estimated_amount ? Math.round(parseFloat(form.estimated_amount) * 100) : null,
        scheduled_date: form.scheduled_date || null,
      };
      delete payload.estimated_amount;
      await api.post("/roofing/jobs", payload);
      toast.success("Job created");
      setForm(initialJob); setShowForm(false); await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this job?")) return;
    await api.delete(`/roofing/jobs/${id}`); await load();
  };

  const advance = async (id, currentStatus) => {
    const order = ["lead","quoted","scheduled","in_progress","complete"];
    const idx = order.indexOf(currentStatus);
    const next = idx >= 0 && idx < order.length - 1 ? order[idx+1] : order[order.length-1];
    try {
      await api.post(`/roofing/jobs/${id}/status`, { status: next });
      toast.success(`Moved to ${STATUS_META[next].label}`); await load();
    } catch { toast.error("Update failed"); }
  };

  const runDiagnose = async () => {
    if (!diagForm.symptoms || diagForm.symptoms.length < 3) {
      toast.error("Describe the damage first"); return;
    }
    setDiagLoading(true); setDiagResult(null);
    try {
      const { data } = await api.post("/roofing/ai-diagnose", {
        ...diagForm,
        roof_age_years: diagForm.roof_age_years ? parseInt(diagForm.roof_age_years, 10) : null,
        sq_footage: diagForm.sq_footage ? parseFloat(diagForm.sq_footage) : null,
      });
      setDiagResult(data.diagnosis);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "AI failed")); }
    finally { setDiagLoading(false); }
  };

  const identifyPhoto = async (file) => {
    if (!file) return;
    setIdLoading(true); setIdResult(null);
    setIdPreview(URL.createObjectURL(file));
    try {
      const fd = new FormData(); fd.append("file", file);
      const url = linkJobId ? `/roofing/ai-photo-id?job_id=${encodeURIComponent(linkJobId)}` : "/roofing/ai-photo-id";
      const r = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setIdResult(r.data.identification);
      if (r.data.journal_saved) toast.success("Saved to job journal");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "ID failed")); }
    finally { setIdLoading(false); }
  };

  const openJournal = async (j) => {
    setJournalFor(j); setJournalData(null);
    try { const r = await api.get(`/roofing/jobs/${j.id}/journal`); setJournalData(r.data); }
    catch { toast.error("Could not load journal"); }
  };
  const downloadPDF = (jobId) => {
    const token = localStorage.getItem("access_token");
    window.open(`${API}/roofing/jobs/${jobId}/estimate.pdf?auth=${encodeURIComponent(token || "")}`, "_blank");
  };

  const sendEstimateSMS = async (job) => {
    if (!job.customer_phone) {
      const p = window.prompt("No phone on file. Enter customer phone:");
      if (!p) return;
      try {
        await api.post(`/roofing/jobs/${job.id}/send-estimate-sms`, { phone_override: p });
        toast.success(`Estimate SMS sent to ${p}`);
        await load();
      } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "SMS failed")); }
      return;
    }
    if (!window.confirm(`Text estimate to ${job.customer_phone}?`)) return;
    try {
      const { data } = await api.post(`/roofing/jobs/${job.id}/send-estimate-sms`, {});
      toast.success(`Estimate sent to ${data.to}`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "SMS failed — check Settings → SMS")); }
  };
  const photoUrl = (path) => {
    const token = localStorage.getItem("access_token");
    return `${API}/files/${path}?auth=${encodeURIComponent(token || "")}`;
  };

  const startJob = (j) => {
    const payload = { id: j.id, name: j.customer_name, address: j.property_address };
    localStorage.setItem("roof_active_job", JSON.stringify(payload));
    setActiveJob(payload); setLinkJobId(j.id);
    toast.success(`On-site at ${payload.name} — photos auto-link`);
  };
  const endJob = () => {
    localStorage.removeItem("roof_active_job");
    setActiveJob(null); setLinkJobId("");
    toast.info("Job ended");
  };

  if (!data) return <div className="p-6 text-slate-500">Loading jobs…</div>;

  const fmtMoney = (cents) => cents ? `$${(cents/100).toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 0})}` : "";

  return (
    <AdminShell>
    <div className="p-4 md:p-6" data-testid="roofing-jobs-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#0f172a] text-white flex items-center justify-center">
            <House weight="fill" size={22} className="text-amber-400"/>
          </div>
          <div>
            <h1 className="font-display font-black text-3xl leading-none">Job Radar</h1>
            <div className="text-xs uppercase font-black tracking-widest text-slate-500 mt-1">
              Roofing pipeline · AI diagnose · photo ID · signed PDF
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="h-11 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 shadow-lg cursor-pointer">
            <Camera weight="fill" size={16}/> {idLoading ? "Identifying…" : "ID Photo"}
            <input type="file" accept="image/*" className="hidden"
              data-testid="roof-photo-id-input"
              onChange={e => { const f = e.target.files?.[0]; if (f) identifyPhoto(f); e.target.value = ""; }}/>
          </label>
          <button onClick={() => { setDiagOpen(true); setDiagResult(null); }} data-testid="roof-diagnose-btn"
            className="h-11 px-4 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 shadow-lg">
            <Sparkle weight="fill" size={16}/> AI Diagnose
          </button>
          <button onClick={() => setShowForm(v => !v)} data-testid="roof-add-btn"
            className="h-11 px-5 bg-[#00C4CC] hover:bg-[#00a9b1] text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 shadow-lg">
            <Plus weight="bold" size={16}/> {showForm ? "Cancel" : "New Job"}
          </button>
        </div>
      </div>

      {activeJob && (
        <div className="mb-4 p-3 rounded-xl bg-fuchsia-600 text-white flex items-center gap-3 shadow-lg" data-testid="roof-active-banner">
          <MapPinLine weight="fill" size={20}/>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80">Currently On-Site · Photos auto-link</div>
            <div className="font-bold text-sm truncate">{activeJob.name} · {activeJob.address}</div>
          </div>
          <button onClick={endJob} data-testid="roof-active-end"
            className="h-8 px-3 bg-white/20 hover:bg-white/30 text-white font-black uppercase tracking-widest text-[10px] rounded-lg">End Job</button>
        </div>
      )}

      {data.weather_scan_at && (
        <div className="mb-3 text-[10px] text-slate-500 flex items-center gap-1.5" data-testid="roof-weather-scan-at">
          <CloudRain weight="fill" size={12} className="text-cyan-500"/>
          Last weather scan: <span className="font-mono">{new Date(data.weather_scan_at).toLocaleString()}</span> · rows with weather risk are flagged inline
        </div>
      )}

      {/* Pipeline value + counters */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4" data-testid="roof-counts">
        <div className="col-span-2 md:col-span-1 p-3 rounded-xl bg-[#0f172a] text-white">
          <div className="text-2xl font-display font-black">{fmtMoney(data.pipeline_value_cents) || "$0"}</div>
          <div className="text-[10px] font-black uppercase tracking-widest opacity-80">Pipeline</div>
        </div>
        {Object.keys(STATUS_META).slice(0,5).map(k => {
          const m = STATUS_META[k];
          return (
            <div key={k} className={`p-3 rounded-xl ${m.color}`}>
              <div className="text-2xl font-display font-black">{data.counts?.[k] ?? 0}</div>
              <div className="text-[10px] font-black uppercase tracking-widest">{m.label}</div>
            </div>
          );
        })}
      </div>

      {showForm && (
        <div className="bg-white border-2 border-[#00C4CC] rounded-2xl p-4 md:p-5 mb-5 shadow-lg" data-testid="roof-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Customer Name *"><input value={form.customer_name} onChange={e => setF("customer_name", e.target.value)} placeholder="Jane Smith" data-testid="roof-cust" className={inputCls}/></Field>
            <Field label="Phone"><input value={form.customer_phone} onChange={e => setF("customer_phone", e.target.value)} placeholder="215-555-0100" data-testid="roof-phone" className={inputCls}/></Field>
            <Field label="Property Address *"><input value={form.property_address} onChange={e => setF("property_address", e.target.value)} placeholder="1200 Sample St" data-testid="roof-addr" className={inputCls}/></Field>
            {/* Roofing Common Fixes — deterministic Philly-flat-roof playbook */}
            <div className="col-span-full" data-testid="roof-common-fixes">
              <div className="text-[10px] uppercase font-black tracking-widest text-red-700 mb-1.5 inline-flex items-center gap-1">
                <Wrench weight="fill" size={11}/> Common Fixes — one-tap fill (12 Philly service calls)
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5 max-h-40 overflow-y-auto">
                {protocolsForRoofKind(form.roof_type, 12).map(p => (
                  <button key={p.id} type="button"
                    data-testid={`roof-quickfix-${p.id}`}
                    onClick={() => {
                      const sys = ROOFING_SYSTEMS.find(s => s.name === p.recommended_product);
                      setForm(prev => ({
                        ...prev,
                        roof_type: p.kind || prev.roof_type,
                        system_product: p.recommended_product,
                        job_type: p.urgency === "high" ? "repair" : prev.job_type,
                        notes: prev.notes || `${p.label}${sys?" · "+sys.warranty:""}${p.warranty_note?" · "+p.warranty_note:""}\nProtocol:\n- ${p.protocol.join("\n- ")}`,
                      }));
                      toast.success(`Protocol · ${p.label}`);
                    }}
                    className={`text-left px-2.5 py-2 border-2 rounded-lg leading-tight ${
                      p.urgency==="high"?"border-red-200 hover:border-red-500 bg-red-50 hover:bg-red-100":
                      p.urgency==="medium"?"border-amber-200 hover:border-amber-500 bg-amber-50 hover:bg-amber-100":
                      "border-slate-200 hover:border-slate-500 bg-slate-50 hover:bg-slate-100"}`}>
                    <div className="text-[11px] font-bold text-slate-900">{p.label}</div>
                    <div className="text-[10px] text-slate-600 mt-0.5 truncate">{p.recommended_product}</div>
                  </button>
                ))}
              </div>
            </div>
            <Field label="Job Type"><select value={form.job_type} onChange={e => setF("job_type", e.target.value)} data-testid="roof-jobtype" className={inputCls}>{(data.job_types||[]).map(t => <option key={t} value={t}>{JOB_TYPE_LABELS[t]||t}</option>)}</select></Field>
            <Field label="Status"><select value={form.status} onChange={e => setF("status", e.target.value)} data-testid="roof-status" className={inputCls}>{(data.statuses||[]).map(s => <option key={s} value={s}>{STATUS_META[s]?.label || s}</option>)}</select></Field>
            <Field label="Roof Type"><select value={form.roof_type} onChange={e => setF("roof_type", e.target.value)} data-testid="roof-rooftype" className={inputCls}>{(data.roof_types||[]).map(t => <option key={t} value={t}>{ROOF_TYPE_LABELS[t]||t}</option>)}</select></Field>
            <Field label="System / Product (optional)">
              <CatalogAutocomplete
                value={form.system_product || ""}
                onChange={v => setF("system_product", v)}
                onSelect={r => {
                  // Auto-map product kind → roof_type dropdown value
                  const kindMap = {
                    tpo: "flat_tpo", epdm: "flat_epdm",
                    modified_bitumen: "flat_modified_bitumen", coating: "other",
                    metal: "metal_standing_seam",
                    shingle_asphalt: "asphalt_architectural",
                    underlayment: form.roof_type,
                  };
                  setForm(prev => ({
                    ...prev,
                    system_product: r.name,
                    roof_type: kindMap[r.kind] || prev.roof_type,
                    notes: prev.notes || `${r.name} · ${r.warranty} · ${r.notes}`,
                  }));
                  toast.success(`${r.brand} ${r.name} · ${r.warranty}`);
                }}
                matchFn={matchRoofingSystems}
                keyFn={(r) => `${r.brand}-${r.name}`}
                testid="roof-system"
                placeholder="Start typing… TPO, GAF, Ruberoid, Timberline"
                disclaimer="Philly-heavy list — flat systems up top. Free-typing is fine."
                renderOption={(r) => (
                  <>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm text-slate-900">{r.name}</span>
                      <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">{r.brand}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{r.warranty} · {r.notes}</div>
                    <div className="mt-1 flex items-center gap-1 flex-wrap">
                      <span className="text-[9px] font-black uppercase tracking-widest bg-red-100 text-red-800 px-1.5 py-0.5 rounded">{r.kind.replace("_", " ")}</span>
                      {r.targets.map(t => (
                        <span key={t} className="text-[9px] font-black uppercase tracking-widest bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{t}</span>
                      ))}
                    </div>
                  </>
                )}
              />
            </Field>
            <Field label="Square Footage"><input type="number" value={form.sq_footage} onChange={e => setF("sq_footage", e.target.value)} placeholder="2200" data-testid="roof-sqft" className={inputCls}/></Field>
            <Field label="Stories"><input type="number" min="1" max="4" value={form.stories} onChange={e => setF("stories", e.target.value)} placeholder="2" data-testid="roof-stories" className={inputCls}/></Field>
            <Field label="Estimated Amount ($)"><input type="number" step="0.01" value={form.estimated_amount} onChange={e => setF("estimated_amount", e.target.value)} placeholder="8500" data-testid="roof-amount" className={inputCls}/></Field>
            <Field label="Scheduled Date"><input type="date" value={form.scheduled_date} onChange={e => setF("scheduled_date", e.target.value)} data-testid="roof-sched" className={inputCls}/></Field>
            <label className="block md:col-span-2">
              <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">Scope / Notes</div>
              <textarea value={form.notes} onChange={e => setF("notes", e.target.value)} rows={3}
                placeholder="Full tear-off, GAF Timberline HDZ architectural shingles, ice & water shield full deck, new drip edge, 30-yr warranty"
                data-testid="roof-notes"
                className="w-full px-3 py-2 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium"/>
            </label>
          </div>
          <button onClick={save} disabled={saving} data-testid="roof-save-btn" className="mt-4 h-11 px-6 bg-[#00C4CC] hover:bg-[#00a9b1] text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 disabled:opacity-50 shadow">
            {saving ? "Saving…" : "Save Job"}
          </button>
        </div>
      )}

      {/* Jobs list */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {data.jobs.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm" data-testid="roof-empty">
            No jobs yet. Tap <b>New Job</b> to add your first lead.
          </div>
        ) : data.jobs.map(j => {
          const m = STATUS_META[j.status] || STATUS_META.lead;
          return (
            <div key={j.id} data-testid={`roof-row-${j.id}`}
              className="p-4 border-b border-slate-100 flex flex-col md:flex-row md:items-center gap-3">
              <div className={`h-9 min-w-[110px] px-2 rounded-lg inline-flex items-center justify-center gap-1 ${m.color}`}>
                <span className="font-black text-[10px] uppercase tracking-widest">{m.label}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-sm">{j.customer_name}</span>
                  <span className="text-[10px] font-black uppercase tracking-widest bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">{JOB_TYPE_LABELS[j.job_type]||j.job_type}</span>
                  {j.roof_type && <span className="text-[10px] font-black uppercase tracking-widest bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">{ROOF_TYPE_LABELS[j.roof_type]||j.roof_type}</span>}
                  {j.estimated_amount_cents > 0 && (
                    <span className="text-[10px] font-black uppercase tracking-widest bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                      <CurrencyDollar weight="bold" size={10}/> {fmtMoney(j.estimated_amount_cents)}
                    </span>
                  )}
                  {j.weather && (
                    <span data-testid={`roof-weather-${j.id}`}
                      title={j.weather.reason}
                      className={`text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${
                        j.weather.risk === "pause" ? "bg-red-500 text-white" : "bg-amber-400 text-slate-900"
                      }`}>
                      <CloudRain weight="fill" size={10}/> {j.weather.risk === "pause" ? "WEATHER PAUSE" : "WEATHER WATCH"}
                    </span>
                  )}
                  {j.estimate_sms_sent_at && (
                    <span className="text-[10px] font-black uppercase tracking-widest bg-cyan-100 text-cyan-800 px-1.5 py-0.5 rounded" title={`Sent ${j.estimate_sms_sent_at}`}>
                      SMS Sent
                    </span>
                  )}
                  {j.journal_count > 0 && (
                    <span className="text-[10px] font-black uppercase tracking-widest bg-fuchsia-100 text-fuchsia-700 px-1.5 py-0.5 rounded">
                      {j.journal_count} photo{j.journal_count===1?"":"s"}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 truncate">{j.property_address}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">
                  {j.sq_footage && `${j.sq_footage} sqft · `}
                  {j.scheduled_date && `Scheduled ${j.scheduled_date}`}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
                {activeJob?.id === j.id ? (
                  <button onClick={endJob} data-testid={`roof-endjob-${j.id}`} className="h-9 px-3 bg-emerald-500 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1 shadow">
                    <MapPinLine weight="fill" size={12}/> On-Site
                  </button>
                ) : (
                  <button onClick={() => startJob(j)} data-testid={`roof-startjob-${j.id}`} className="h-9 px-3 bg-fuchsia-100 hover:bg-fuchsia-200 text-fuchsia-800 font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1">
                    <MapPinLine weight="fill" size={12}/> Start Job
                  </button>
                )}
                <button onClick={() => openJournal(j)} data-testid={`roof-journal-${j.id}`} className="h-9 px-3 bg-slate-800 hover:bg-slate-900 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1 shadow">
                  <Camera weight="fill" size={12}/> Journal
                </button>
                {j.estimated_amount_cents > 0 && (
                  <button onClick={() => sendEstimateSMS(j)} data-testid={`roof-sms-${j.id}`}
                    className="h-9 px-3 bg-cyan-600 hover:bg-cyan-700 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1 shadow"
                    title="Text estimate PDF to customer">
                    <ChatCircleText weight="fill" size={12}/> Text Estimate
                  </button>
                )}
                {j.status !== "complete" && j.status !== "declined" && (
                  <button onClick={() => advance(j.id, j.status)} data-testid={`roof-advance-${j.id}`} className="h-9 px-3 bg-cyan-600 hover:bg-cyan-700 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1 shadow">
                    <CaretRight weight="bold" size={12}/> Advance
                  </button>
                )}
                <button onClick={() => remove(j.id)} data-testid={`roof-remove-${j.id}`} className="h-9 w-9 rounded-lg bg-slate-100 hover:bg-red-100 hover:text-red-600 text-slate-400 inline-flex items-center justify-center">
                  <Trash size={14}/>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Diagnose Modal */}
      {diagOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" onClick={() => !diagLoading && setDiagOpen(false)} data-testid="roof-diag-modal">
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-2"><Sparkle weight="fill" size={22} className="text-fuchsia-500"/><div className="font-display font-black text-xl">AI Damage Diagnose</div></div>
              <button onClick={() => setDiagOpen(false)} className="text-slate-400 hover:text-slate-800" data-testid="roof-diag-close"><X size={20}/></button>
            </div>
            <div className="p-5">
              {!diagResult ? (
                <>
                  <div className="text-xs text-slate-500 mb-3">Describe what you're seeing on the roof — AI suggests likely causes, scope, and insurance angles.</div>
                  <div className="space-y-3">
                    <Field label="Roof Type"><select value={diagForm.roof_type} onChange={e => setDiagForm(p => ({ ...p, roof_type: e.target.value }))} data-testid="roof-diag-type" className={inputCls}>{(data.roof_types||[]).map(t => <option key={t} value={t}>{ROOF_TYPE_LABELS[t]||t}</option>)}</select></Field>
                    <Field label="Symptoms *"><textarea rows={3} value={diagForm.symptoms} onChange={e => setDiagForm(p => ({ ...p, symptoms: e.target.value }))} placeholder="Missing shingles on north slope, exposed felt visible, some circular bruising on other shingles suggests hail" data-testid="roof-diag-symptoms" className="w-full px-3 py-2 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium"/></Field>
                    <div className="grid grid-cols-2 gap-2">
                      <Field label="Roof Age (yrs)"><input type="number" value={diagForm.roof_age_years} onChange={e => setDiagForm(p => ({ ...p, roof_age_years: e.target.value }))} placeholder="15" data-testid="roof-diag-age" className={inputCls}/></Field>
                      <Field label="Sq Footage"><input type="number" value={diagForm.sq_footage} onChange={e => setDiagForm(p => ({ ...p, sq_footage: e.target.value }))} placeholder="2200" data-testid="roof-diag-sqft" className={inputCls}/></Field>
                    </div>
                  </div>
                  <button onClick={runDiagnose} disabled={diagLoading} data-testid="roof-diag-submit" className="mt-4 w-full h-12 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center justify-center gap-2 disabled:opacity-50 shadow">
                    <Sparkle weight="fill" size={14}/> {diagLoading ? "Thinking…" : "Diagnose"}
                  </button>
                </>
              ) : (
                <div data-testid="roof-diag-result">
                  <div className="text-[10px] uppercase font-black tracking-widest text-fuchsia-600 mb-1">Diagnosis · Confidence: {diagResult.confidence}</div>
                  {diagResult.recommended_scope && (
                    <div className="mb-3 p-3 rounded-lg bg-cyan-50 border-l-4 border-cyan-500">
                      <div className="text-[10px] uppercase font-black tracking-widest text-cyan-700 mb-1">Recommended Scope</div>
                      <div className="font-bold text-base">{diagResult.recommended_scope.replace(/_/g, ' ').toUpperCase()}</div>
                      <div className="text-xs text-slate-600 mt-1">{diagResult.scope_reason}</div>
                    </div>
                  )}
                  {(diagResult.likely_causes || []).map((c, i) => (
                    <div key={i} className="border-l-4 border-fuchsia-500 pl-3 py-1 mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-sm">{c.cause}</span>
                        <span className={`text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${c.probability==="high"?"bg-red-100 text-red-700":c.probability==="low"?"bg-slate-100 text-slate-700":"bg-amber-100 text-amber-700"}`}>{c.probability}</span>
                        {c.damage && <span className="text-[10px] font-mono text-slate-500">→ {c.damage}</span>}
                      </div>
                      <div className="text-xs text-slate-600 mt-0.5">{c.why}</div>
                    </div>
                  ))}
                  {(diagResult.checks || []).length > 0 && (
                    <div className="mb-3"><div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">Verify Onsite</div><ul className="list-disc pl-5 text-sm space-y-1">{diagResult.checks.map((s, i) => <li key={i}>{s}</li>)}</ul></div>
                  )}
                  {(diagResult.insurance_notes || []).length > 0 && (
                    <div className="mb-3 p-3 rounded-lg bg-amber-50 border-l-4 border-amber-500"><div className="text-[10px] uppercase font-black tracking-widest text-amber-700 mb-1">Insurance Signals</div><ul className="list-disc pl-5 text-sm space-y-1 text-amber-900">{diagResult.insurance_notes.map((s, i) => <li key={i}>{s}</li>)}</ul></div>
                  )}
                  {(diagResult.safety || []).length > 0 && (
                    <div className="mb-3"><div className="text-[10px] uppercase font-black tracking-widest text-red-700 mb-1">Safety</div><ul className="list-disc pl-5 text-sm space-y-1 text-red-800">{diagResult.safety.map((s, i) => <li key={i}>{s}</li>)}</ul></div>
                  )}
                  <button onClick={() => setDiagResult(null)} className="mt-2 w-full h-10 border-2 border-slate-300 hover:bg-slate-100 text-slate-700 font-black uppercase tracking-widest text-xs rounded-xl">Ask Again</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Photo ID Modal */}
      {(idResult || idLoading) && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" onClick={() => !idLoading && (setIdResult(null), setIdPreview(null))} data-testid="roof-id-modal">
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-2"><Camera weight="fill" size={22} className="text-emerald-500"/><div className="font-display font-black text-xl">Photo-to-Shingle ID</div></div>
              {!idLoading && <button onClick={() => { setIdResult(null); setIdPreview(null); }} className="text-slate-400 hover:text-slate-800" data-testid="roof-id-close"><X size={20}/></button>}
            </div>
            <div className="p-5">
              {idPreview && <img src={idPreview} alt="damage" className="w-full max-h-56 object-contain rounded-lg mb-4 bg-slate-50"/>}
              {idLoading ? (
                <div className="text-center py-6"><div className="text-sm font-bold text-slate-600 mb-2">Analyzing photo…</div></div>
              ) : idResult && (
                <div data-testid="roof-id-result">
                  <div className="text-[10px] uppercase font-black tracking-widest text-emerald-600 mb-1">Identified · Confidence: {idResult.confidence}</div>
                  <div className="font-display font-black text-2xl leading-tight">{idResult.damage_specific || (idResult.damage || "unknown").replace(/_/g," ")}</div>
                  <div className="text-xs text-slate-500 mt-1">Roof: {ROOF_TYPE_LABELS[idResult.roof_type] || idResult.roof_type || "—"}</div>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    {idResult.severity && <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded ${idResult.severity==="catastrophic"?"bg-red-100 text-red-800":idResult.severity==="severe"?"bg-red-100 text-red-800":idResult.severity==="moderate"?"bg-amber-100 text-amber-800":"bg-slate-100 text-slate-700"}`}>{idResult.severity}</span>}
                    {idResult.urgency && <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded ${idResult.urgency==="urgent"?"bg-red-100 text-red-800":"bg-cyan-100 text-cyan-800"}`}>{idResult.urgency}</span>}
                    {idResult.estimated_age_range && <span className="text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded bg-slate-100 text-slate-700">Est. {idResult.estimated_age_range}</span>}
                  </div>
                  {idResult.insurance_relevant && (
                    <div className="mt-3 p-2 rounded-lg bg-amber-50 border-l-4 border-amber-500 text-amber-900 text-sm">
                      <Warning weight="fill" size={14} className="inline mr-1"/> Possible insurance claim indicator — document thoroughly
                    </div>
                  )}
                  {(idResult.observed || []).length > 0 && (
                    <div className="mt-4"><div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">What AI Saw</div><ul className="list-disc pl-5 text-sm space-y-1">{idResult.observed.map((s, i) => <li key={i}>{s}</li>)}</ul></div>
                  )}
                  {idResult.next_step && (
                    <div className="mt-4 p-3 rounded-lg bg-fuchsia-50 border-l-4 border-fuchsia-500 text-fuchsia-900 text-sm"><b>Next step:</b> {idResult.next_step}</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Journal Modal */}
      {journalFor && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" onClick={() => { setJournalFor(null); setJournalData(null); }} data-testid="roof-journal-modal">
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-2"><House weight="fill" size={22} className="text-amber-500"/>
                <div><div className="font-display font-black text-xl">{journalFor.customer_name}</div><div className="text-[11px] text-slate-500 font-mono">{journalFor.property_address}</div></div>
              </div>
              <button onClick={() => { setJournalFor(null); setJournalData(null); }} className="text-slate-400 hover:text-slate-800" data-testid="roof-journal-close"><X size={20}/></button>
            </div>
            <div className="p-5">
              {!journalData ? <div className="text-center text-slate-400 py-6">Loading…</div> : (
                <>
                  <button onClick={() => downloadPDF(journalFor.id)} data-testid="roof-pdf-btn" className="w-full h-10 mb-4 bg-red-600 hover:bg-red-700 text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center justify-center gap-2 shadow">
                    <FilePdf weight="fill" size={14}/> Download Signed Estimate PDF
                  </button>
                  {journalData.entries.length === 0 ? (
                    <div className="text-center text-slate-400 py-6" data-testid="roof-journal-empty">
                      No inspection photos yet. Tap <b>Start Job</b> then <b>ID Photo</b>.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {journalData.entries.map((e, i) => {
                        const id = e.identification || {};
                        return (
                          <div key={i} data-testid={`roof-journal-entry-${i}`} className="flex gap-3 border border-slate-200 rounded-xl p-3">
                            {e.photo_path && <img src={photoUrl(e.photo_path)} alt="damage" className="w-24 h-24 object-cover rounded-lg bg-slate-100 flex-shrink-0"/>}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-bold text-sm">{id.damage_specific || (id.damage || "finding").replace(/_/g," ")}</span>
                                {id.severity && <span className={`text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${id.severity==="severe"||id.severity==="catastrophic"?"bg-red-100 text-red-700":id.severity==="moderate"?"bg-amber-100 text-amber-700":"bg-slate-100 text-slate-700"}`}>{id.severity}</span>}
                                {id.insurance_relevant && <span className="text-[10px] font-black uppercase tracking-widest bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Ins.</span>}
                              </div>
                              <div className="text-[11px] text-slate-500 mt-0.5 font-mono">{new Date(e.captured_at).toLocaleString()} · {e.captured_by || ""}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    </AdminShell>
  );
}

const inputCls = "w-full h-11 px-3 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium";
function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}
